import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { App, type MenuProps } from 'antd'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'

export type BusinessActionRow = {
  id: number
  isEnabled: boolean
}

type BusinessListActionsOptions = {
  currentPage: number
  rows: BusinessActionRow[]
  invalidate: () => Promise<unknown>
  goToPreviousPage: () => Promise<unknown>
}

export function useBusinessListActions({
  currentPage,
  goToPreviousPage,
  invalidate,
  rows
}: BusinessListActionsOptions) {
  const trpc = useTRPC()
  const { message, modal } = App.useApp()
  const [selectedBusinessIds, setSelectedBusinessIds] = useState<number[]>([])
  const setEnabled = useMutation(trpc.business.setEnabled.mutationOptions())
  const batchSetEnabled = useMutation(trpc.business.batchSetEnabled.mutationOptions())
  const remove = useMutation(trpc.business.remove.mutationOptions())
  const batchRemove = useMutation(trpc.business.batchRemove.mutationOptions())

  const clearSelection = () => setSelectedBusinessIds([])

  const changeStatus = async (business: BusinessActionRow) => {
    try {
      await setEnabled.mutateAsync({ id: business.id, isEnabled: !business.isEnabled })
      void message.success(business.isEnabled ? '业务已停用' : '业务已启用')
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '业务状态更新失败'))
    }
  }

  const changeSelectedStatus = async (isEnabled: boolean) => {
    try {
      const result = await batchSetEnabled.mutateAsync({ ids: selectedBusinessIds, isEnabled })
      clearSelection()
      void message.success(`已${isEnabled ? '启用' : '停用'} ${result.count} 个业务`)
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '批量更新业务状态失败'))
    }
  }

  const removeBusiness = async (business: BusinessActionRow) => {
    try {
      await remove.mutateAsync({ id: business.id })
      void message.success('业务已删除')
      const shouldGoBack = rows.length === 1 && currentPage > 1
      await invalidate()
      if (shouldGoBack) {
        await goToPreviousPage()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '业务删除失败'))
    }
  }

  const confirmRemoveBusiness = (business: BusinessActionRow) => {
    modal.confirm({
      content: '删除后将从业务列表和可选业务中移除，历史订单数据仍会保留。',
      okButtonProps: { danger: true },
      onOk: () => removeBusiness(business),
      title: '确认删除该业务？'
    })
  }

  const confirmChangeStatus = (business: BusinessActionRow) => {
    modal.confirm({
      content: business.isEnabled ? '停用后将不能用于新的服务订单，历史数据仍会保留。' : undefined,
      okButtonProps: business.isEnabled ? { danger: true } : undefined,
      onOk: () => changeStatus(business),
      title: `确认${business.isEnabled ? '停用' : '启用'}业务？`
    })
  }

  const removeSelectedBusinesses = async () => {
    try {
      const result = await batchRemove.mutateAsync({ ids: selectedBusinessIds })
      const allVisibleRowsSelected = selectedBusinessIds.length === rows.length && rows.length > 0
      clearSelection()
      void message.success(`已删除 ${result.count} 个业务`)
      await invalidate()
      if (allVisibleRowsSelected && currentPage > 1) {
        await goToPreviousPage()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '批量删除业务失败'))
    }
  }

  const confirmChangeSelectedStatus = (isEnabled: boolean) => {
    if (isEnabled) {
      void changeSelectedStatus(true)
      return
    }
    modal.confirm({
      content: '停用后，选中的业务不能继续用于新的服务订单，历史数据仍会保留。',
      okButtonProps: { danger: true },
      okText: '确认停用',
      onOk: () => changeSelectedStatus(false),
      title: `停用已选 ${selectedBusinessIds.length} 个业务？`
    })
  }

  const confirmRemoveSelectedBusinesses = () => {
    modal.confirm({
      content: '删除后将从业务列表和可选业务中移除，历史订单数据仍会保留。',
      okButtonProps: { danger: true },
      okText: '确认删除',
      onOk: removeSelectedBusinesses,
      title: `删除已选 ${selectedBusinessIds.length} 个业务？`
    })
  }

  const batchMenu: MenuProps = {
    items: [
      { key: 'enable', label: '批量启用' },
      { danger: true, key: 'disable', label: '批量停用' },
      { type: 'divider' as const },
      { danger: true, key: 'delete', label: '批量删除' }
    ],
    onClick: ({ key }) => {
      if (key === 'delete') {
        confirmRemoveSelectedBusinesses()
        return
      }
      confirmChangeSelectedStatus(key === 'enable')
    }
  }

  return {
    batchMenu,
    clearSelection,
    confirmChangeStatus,
    confirmRemoveBusiness,
    selectedBusinessIds,
    setSelectedBusinessIds
  }
}
